import 'client-only';

import DocumentsApiSdk, { ClientInferRequest, contract } from '@pie/documents-api-sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { env } from '@/config/env';
import { ApiError } from '@/lib/api/ApiError';
import type { ListPolicyDocumentsParams, PolicyDocument } from './types';
import { useAuthContext } from '@/components/AuthContext';

export * from './types';

const { client } = new DocumentsApiSdk({ environment: env.NEXT_PUBLIC_PIE_ENV });

const QUERY_KEYS = {
  DOCUMENTS: 'documents',
} as const;

export const queryKeys = {
  listPolicyDocuments: ({ policyId }: ListPolicyDocumentsParams) =>
    [QUERY_KEYS.DOCUMENTS, 'list', policyId] as const,
  paymentScheduleDocument: (policyId: string) =>
    [QUERY_KEYS.DOCUMENTS, 'payment-schedule', policyId] as const,
};

export const listPolicyDocuments = async (
  params: ListPolicyDocumentsParams,
  headers: ClientInferRequest<typeof contract.getDocuments>['headers']
): Promise<PolicyDocument[]> => {
  const response = await client.getDocuments({
    headers,
    query: { policyId: params.policyId },
  });

  if (response.status !== 200) {
    throw new ApiError(response.status, response.body, response.headers);
  }

  return response.body.map(toPolicyDocument);
};

// TODO: Replace with `ClientInferResponses<typeof contract.getDocuments>` once
// @pie/documents-api-sdk exports `contract` (see billing-api-sdk pattern).
const toPolicyDocument = (api: {
  id: string;
  fileName: string;
  type: string;
  date: string;
  subDocumentIds?: string[];
}): PolicyDocument => ({
  documentId: api.id,
  name: api.fileName,
  type: api.type,
  dateCreated: api.date.split('T')[0],
});

export const generateStandardCoi = async (
  policyId: string,
  headers: ClientInferRequest<typeof contract.getDocuments>['headers']
): Promise<{ url: string; expiresAt: string }> => {
  const response = await client.generateDocument({
    headers,
    body: { type: 'instant-coi', policyId },
  });

  if (response.status !== 200) {
    throw new ApiError(response.status, response.body, response.headers);
  }

  return response.body;
};

export const useGenerateStandardCoi = (policyId: string) => {
  const { getSessionHeaders } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    // TODO(CORE-541): this is a hack to work around the limitations of the document service,
    //   the ideal state is to generate a new COI, respond with new document including documentId
    //   and name for download / view, and then immediately refetch the list of documents, however
    //   the document service generate endpoint is unable to return a documentId and name at this
    //   time and there are some backend processing that need to happen that delays the new document
    //   from appearing in the list of documents, so to provide the desired user experience, this
    //   hack just waits until the new document appears in the list of documents
    mutationFn: async () => {
      const headers = await getSessionHeaders();
      const beforeDocuments = await listPolicyDocuments({ policyId }, headers);
      const beforeDocumentIds = beforeDocuments.map(({ documentId }) => documentId);

      await generateStandardCoi(policyId, headers);

      for (let attempt = 0; attempt < 5; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 2_000));

        const documents = await listPolicyDocuments({ policyId }, headers);
        const newCoiDocument = documents.find(
          (document) => document.type === 'COI' && !beforeDocumentIds.includes(document.documentId)
        );

        if (newCoiDocument) {
          return newCoiDocument;
        }
      }

      throw new Error('Unable to generate a Certificate of Insurance at this time.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.listPolicyDocuments({ policyId }) });
    },
  });
};

export const usePaymentScheduleDocument = (policyId: string) => {
  const { getSessionHeaders } = useAuthContext();

  return useQuery({
    queryKey: queryKeys.paymentScheduleDocument(policyId),
    queryFn: async () => {
      const headers = await getSessionHeaders();
      const response = await client.getDocuments({
        headers,
        query: { policyId, type: 'installment-schedule' },
      });

      if (response.status !== 200) {
        throw new ApiError(response.status, response.body, response.headers);
      }

      return response.body[0] ? toPolicyDocument(response.body[0]) : null;
    },
  });
};

export const useListPolicyDocuments = (params: ListPolicyDocumentsParams) => {
  const { getSessionHeaders } = useAuthContext();

  const response = useQuery<PolicyDocument[]>({
    queryKey: queryKeys.listPolicyDocuments(params),
    queryFn: async () => {
      const headers = await getSessionHeaders();
      return listPolicyDocuments(params, headers);
    },
  });

  return response;
};
